import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import {
  internalSearchFile,
  internalSearchFileContent,
  SearchFileContentRequest,
  SearchFileRequest
} from "src/network/graphql/bulkUploadService";
import { getActivity, ignoreTransaction, settleTransaction } from "src/network/graphql/transactionService";
import { getWalletDetail } from "src/network/graphql/walletService";
import { formatFullName } from "src/utils";
import {
  BulkUploadFile,
  BulkUploadFileContent,
  BulkUploadFileContentListing,
  BulkUploadFileListing,
  parseFileContentAttributes,
  parseFileContentContent
} from "../bulkUpload/bulkUploadTypes";

export interface SettlementWalletParticipant {
  id: string;
  fullName: string;
  email: string;
}

export interface SettlementWalletGroup {
  group_id: string;
  name: string;
  group_type: string;
}

export interface SettlementWalletDetail {
  id: string;
  displayId: string;
  name: string;
  balance: number;
  participant: SettlementWalletParticipant | null;
  group: SettlementWalletGroup | null;
}

interface InternalBulkUploadState {
  fileListLoading: boolean;
  fileListError: string | null;
  files: BulkUploadFileListing;

  detailFileLoading: boolean;
  detailFileError: string | null;
  detailFile: BulkUploadFile | null;

  contentListLoading: boolean;
  contentListError: string | null;
  contents: BulkUploadFileContentListing;

  settlementWalletDetail: SettlementWalletDetail | null;
  settlementWalletDetailLoading: boolean;
  settleMismatchLoading: boolean;
  ignoreMismatchLoading: boolean;
  reloadFileContent: boolean;
}

const initialState: InternalBulkUploadState = {
  fileListLoading: false,
  fileListError: null,
  files: { list: [], total: 0 },

  detailFileLoading: false,
  detailFileError: null,
  detailFile: null,

  contentListLoading: false,
  contentListError: null,
  contents: { list: [], total: 0 },

  settlementWalletDetail: null,
  settlementWalletDetailLoading: false,
  settleMismatchLoading: false,
  ignoreMismatchLoading: false,
  reloadFileContent: false
};

const internalBulkUploadSlice = createSlice({
  name: "internalBulkUpload",
  initialState,
  reducers: {
    fetchFilesStart: (state) => {
      state.fileListLoading = true;
      state.fileListError = null;
    },
    fetchFilesSuccess: (state, action: PayloadAction<BulkUploadFileListing>) => {
      state.fileListLoading = false;
      state.files = action.payload;
    },
    fetchFilesFail: (state, action: PayloadAction<string>) => {
      state.fileListLoading = false;
      state.fileListError = action.payload;
      state.files = { list: [], total: 0 };
    },
    fetchDetailFileStart: (state) => {
      state.detailFileLoading = true;
      state.detailFileError = null;
    },
    fetchDetailFileSuccess: (state, action: PayloadAction<BulkUploadFile | null>) => {
      state.detailFileLoading = false;
      state.detailFile = action.payload;
    },
    fetchDetailFileFail: (state, action: PayloadAction<string>) => {
      state.detailFileLoading = false;
      state.detailFileError = action.payload;
      state.detailFile = null;
    },
    fetchContentsStart: (state) => {
      state.contentListLoading = true;
      state.contentListError = null;
    },
    fetchContentsSuccess: (state, action: PayloadAction<BulkUploadFileContentListing>) => {
      state.contentListLoading = false;
      state.contents = action.payload;
    },
    fetchContentsFail: (state, action: PayloadAction<string>) => {
      state.contentListLoading = false;
      state.contentListError = action.payload;
      state.contents = { list: [], total: 0 };
    },
    resetInternalBulkUploadDetail: (state) => {
      state.detailFile = null;
      state.detailFileError = null;
      state.contents = { list: [], total: 0 };
      state.contentListError = null;
    },
    fetchSettlementWalletDetailStart: (state) => {
      state.settlementWalletDetailLoading = true;
    },
    fetchSettlementWalletDetailSuccess: (state, action: PayloadAction<SettlementWalletDetail | null>) => {
      state.settlementWalletDetailLoading = false;
      state.settlementWalletDetail = action.payload;
    },
    fetchSettlementWalletDetailFail: (state) => {
      state.settlementWalletDetailLoading = false;
      state.settlementWalletDetail = null;
    },
    resetSettlementWalletDetail: (state) => {
      state.settlementWalletDetail = null;
      state.settlementWalletDetailLoading = false;
    },
    settleMismatchStart: (state) => {
      state.settleMismatchLoading = true;
    },
    settleMismatchSuccess: (state) => {
      state.settleMismatchLoading = false;
    },
    settleMismatchFail: (state) => {
      state.settleMismatchLoading = false;
    },
    ignoreMismatchStart: (state) => {
      state.ignoreMismatchLoading = true;
    },
    ignoreMismatchSuccess: (state) => {
      state.ignoreMismatchLoading = false;
    },
    ignoreMismatchFail: (state) => {
      state.ignoreMismatchLoading = false;
    },
    setReloadFileContent: (state, action: PayloadAction<boolean>) => {
      state.reloadFileContent = action.payload;
    }
  }
});

export const {
  fetchFilesStart,
  fetchFilesSuccess,
  fetchFilesFail,
  fetchDetailFileStart,
  fetchDetailFileSuccess,
  fetchDetailFileFail,
  fetchContentsStart,
  fetchContentsSuccess,
  fetchContentsFail,
  resetInternalBulkUploadDetail,
  fetchSettlementWalletDetailStart,
  fetchSettlementWalletDetailSuccess,
  fetchSettlementWalletDetailFail,
  resetSettlementWalletDetail,
  settleMismatchStart,
  settleMismatchSuccess,
  settleMismatchFail,
  ignoreMismatchStart,
  ignoreMismatchSuccess,
  ignoreMismatchFail,
  setReloadFileContent
} = internalBulkUploadSlice.actions;

const toErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "Something went wrong";
};

export const fetchInternalBulkUploadFilesAction = (data: SearchFileRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchFilesStart());
    try {
      const response = await internalSearchFile(data);
      const payload = response.data?.internal_search_file ?? {};
      const files: BulkUploadFile[] = payload.files ?? [];

      dispatch(
        fetchFilesSuccess({
          list: files,
          total: payload.total_size ?? files.length
        })
      );
    } catch (error) {
      dispatch(fetchFilesFail(toErrorMessage(error)));
    }
  };
};

export const fetchInternalBulkUploadDetailFileAction = (fileId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchDetailFileStart());
    try {
      const response = await internalSearchFile({
        from: 0,
        size: 1,
        filter: { file_ids: [fileId] }
      });
      const file: BulkUploadFile | undefined = response.data?.internal_search_file?.files?.[0];

      dispatch(fetchDetailFileSuccess(file ?? null));
    } catch (error) {
      dispatch(fetchDetailFileFail(toErrorMessage(error)));
    }
  };
};

export const fetchInternalBulkUploadContentsAction = (data: SearchFileContentRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchContentsStart());
    try {
      const response = await internalSearchFileContent(data);
      const payload = response.data?.internal_search_file_content ?? {};
      const rawList: BulkUploadFileContent[] = payload.file_content_list ?? [];

      const normalised = rawList.map((row) => ({
        ...row,
        attributes: parseFileContentAttributes(row.attributes),
        content: parseFileContentContent(row.content)
      }));

      dispatch(
        fetchContentsSuccess({
          list: normalised,
          total: payload.total_size ?? normalised.length
        })
      );
    } catch (error) {
      dispatch(fetchContentsFail(toErrorMessage(error)));
    }
  };
};

export const fetchSettlementWalletDetail = (linkId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchSettlementWalletDetailStart());
    try {
      const activityResponse = await getActivity({ activity_id: linkId });
      const activityData = JSON.parse(activityResponse.data.get_activity.data);
      const walletId = activityData?.wallet_id;

      if (!walletId) {
        dispatch(fetchSettlementWalletDetailFail());
        return;
      }

      const walletResponse = await getWalletDetail(walletId);
      const wallet = walletResponse.data.get_wallet;

      const detail: SettlementWalletDetail = {
        id: wallet?.wallet_id ?? "",
        displayId: wallet?.display_id ?? "",
        name: wallet?.name ?? "",
        balance: typeof wallet?.balance === "number" ? wallet.balance : Number(wallet?.balance ?? 0),
        participant: wallet?.user
          ? {
              id: wallet.user.user_id,
              fullName: formatFullName(wallet.user?.attributes?.first_name, wallet.user?.attributes?.last_name),
              email: wallet.user?.email ?? ""
            }
          : null,
        group: wallet?.group
          ? {
              group_id: wallet.group.group_id,
              name: wallet.group.name,
              group_type: wallet.group.group_type
            }
          : null
      };

      dispatch(fetchSettlementWalletDetailSuccess(detail));
    } catch (error) {
      dispatch(fetchSettlementWalletDetailFail());
    }
  };
};

export const settleSettlementMismatch = (contentId: string, onSuccess?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(settleMismatchStart());
    try {
      await settleTransaction(contentId);
      dispatch(settleMismatchSuccess());
      dispatch(setReloadFileContent(true));
      onSuccess?.();
    } catch (error) {
      dispatch(settleMismatchFail());
    }
  };
};

export const ignoreSettlementMismatch = (
  contentId: string,
  reason: string,
  note?: string,
  onSuccess?: () => void
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(ignoreMismatchStart());
    try {
      await ignoreTransaction(contentId, reason, note);
      dispatch(ignoreMismatchSuccess());
      dispatch(setReloadFileContent(true));
      onSuccess?.();
    } catch (error) {
      dispatch(ignoreMismatchFail());
    }
  };
};

export default internalBulkUploadSlice.reducer;
